import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../setup/axios/axiosInstance';
import { useToast } from '../../modules/layout/components/ToastContext';
import Pagination from '../../modules/layout/components/Pagination';

interface ITransactionOption {
  className: string;
  serviceName: string;
  institutionId: string;
  institutionName: string;
}

interface ServiceGroup {
  serviceName: string;
  institutionName: string;
  transactions: ITransactionOption[];
}

interface SpokeAllowedTransactionsProps {
  spokeUserId: string;
}

const groupByService = (
  items: ITransactionOption[],
  allowed: Set<string>
): ServiceGroup[] => {
  const map = new Map<string, ServiceGroup>();
  for (const item of items) {
    const key = item.serviceName || '—';
    if (!map.has(key)) {
      map.set(key, {
        serviceName: item.serviceName || '—',
        institutionName: item.institutionName || '',
        transactions: []
      });
    }
    map.get(key)!.transactions.push(item);
  }

  const groups = Array.from(map.values());
  groups.forEach((group) => {
    group.transactions.sort((a, b) => {
      const aAllowed = allowed.has(a.className.toUpperCase()) ? 0 : 1;
      const bAllowed = allowed.has(b.className.toUpperCase()) ? 0 : 1;
      if (aAllowed !== bAllowed) return aAllowed - bAllowed;
      return a.className.localeCompare(b.className);
    });
  });

  return groups.sort((a, b) => {
    const aCount = a.transactions.filter((t) => allowed.has(t.className.toUpperCase())).length;
    const bCount = b.transactions.filter((t) => allowed.has(t.className.toUpperCase())).length;
    if (aCount !== bCount) return bCount - aCount;
    return a.serviceName.localeCompare(b.serviceName);
  });
};

export default function SpokeAllowedTransactions({ spokeUserId }: SpokeAllowedTransactionsProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const [options, setOptions] = useState<ITransactionOption[]>([]);
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const load = async () => {
      if (!spokeUserId) return;
      setLoading(true);
      try {
        const [optionsResp, allowedResp] = await Promise.all([
          axiosInstance.get('/api/TransactionRoutes/classnameoptions'),
          axiosInstance.get(`/api/SpokePermissions/allowed-transactions?spokeUserId=${encodeURIComponent(spokeUserId)}`)
        ]);

        setOptions(Array.isArray(optionsResp.data) ? optionsResp.data : []);

        const data: ITransactionOption[] = Array.isArray(allowedResp.data) ? allowedResp.data : [];
        const set = new Set(data.map((c) => c.className.toUpperCase()));
        setAllowed(set);
        setPending(new Set(set));
      } catch (err) {
        showToast && showToast(t('SomethingWentWrong'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [spokeUserId]);

  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handler);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.classList.remove('modal-open');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.className.toLowerCase().includes(q) ||
        (o.serviceName || '').toLowerCase().includes(q) ||
        (o.institutionName || '').toLowerCase().includes(q)
    );
  }, [options, search]);

  const editGroups = useMemo(
    () => groupByService(filteredOptions, allowed),
    [filteredOptions, allowed]
  );

  const pagedGroups = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return editGroups.slice(start, start + pageSize);
  }, [editGroups, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(editGroups.length / pageSize));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [editGroups.length, pageSize, currentPage]);

  const allowedCount = allowed.size;

  const orphanAllowed = useMemo(() => {
    const known = new Set(options.map((o) => o.className.toUpperCase()));
    return Array.from(allowed).filter((c) => !known.has(c));
  }, [options, allowed]);

  const distinctServicesAllowed = useMemo(() => {
    const seen = new Set<string>();
    for (const o of options) {
      if (allowed.has(o.className.toUpperCase())) {
        seen.add(o.serviceName);
      }
    }
    return seen.size;
  }, [options, allowed]);

  const toggleClass = (className: string) => {
    const key = className.toUpperCase();
    const next = new Set(pending);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPending(next);
  };

  const toggleExpanded = (serviceKey: string) => {
    const next = new Set(expanded);
    if (next.has(serviceKey)) next.delete(serviceKey);
    else next.add(serviceKey);
    setExpanded(next);
  };

  const setGroup = (group: ServiceGroup, select: boolean) => {
    const next = new Set(pending);
    for (const tx of group.transactions) {
      const key = tx.className.toUpperCase();
      if (select) next.add(key);
      else next.delete(key);
    }
    setPending(next);
  };

  const groupCounts = (group: ServiceGroup) => {
    let selected = 0;
    for (const tx of group.transactions) {
      if (pending.has(tx.className.toUpperCase())) selected++;
    }
    return { selected, total: group.transactions.length };
  };

  const openModal = () => {
    setPending(new Set(allowed));
    setSearch('');
    setExpanded(new Set());
    setCurrentPage(1);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setSearch('');
    setExpanded(new Set());
    setCurrentPage(1);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = Array.from(pending);
      await axiosInstance.put(
        `/api/SpokePermissions/allowed-transactions?spokeUserId=${encodeURIComponent(spokeUserId)}`,
        payload
      );
      setAllowed(new Set(pending));
      setModalOpen(false);
      setSearch('');
      setExpanded(new Set());
      showToast && showToast(t('AllowedClassesUpdatedSuccessfully'), 'success');
    } catch (err) {
      showToast && showToast(t('SomethingWentWrong'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light py-3">
          <h6 className="mb-0 fw-bold">{t('AllowedTransactions')}</h6>
        </div>
        <div className="card-body d-grid gap-3">
          {loading ? (
            <div className="d-flex justify-content-center py-2">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">{t('Loading')}</span>
              </div>
            </div>
          ) : (
            <>
              <div>
                {allowedCount === 0 ? (
                  <span className="badge bg-success">
                    {t('AllowedTransactionsStatusUnrestricted')}
                  </span>
                ) : (
                  <>
                    <span className="badge bg-warning text-dark me-2">
                      {t('AllowedTransactionsStatusRestricted', {
                        count: allowedCount,
                        services: distinctServicesAllowed
                      })}
                    </span>
                    {orphanAllowed.length > 0 && (
                      <span className="badge bg-danger">
                        {t('AllowedTransactionsStatusOrphanCount', {
                          count: orphanAllowed.length
                        })}
                      </span>
                    )}
                  </>
                )}
              </div>
              <button
                type="button"
                className="btn btn-dark"
                onClick={openModal}
                disabled={loading}
              >
                {t('AllowedTransactionsManage')}
              </button>
            </>
          )}
        </div>
      </div>

      {modalOpen && (
        <>
          <div
            className="modal d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeModal();
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title">{t('AllowedTransactions')}</h5>
                    <small className="text-muted d-block mt-1">
                      {t('AllowedTransactionsHint')}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeModal}
                    disabled={saving}
                  />
                </div>
                <div className="modal-body">
                  <input
                    type="text"
                    className="form-control form-control-sm mb-3"
                    placeholder={t('Search')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {editGroups.length === 0 ? (
                    <div className="text-muted small fst-italic">{t('NoResults')}</div>
                  ) : (
                    pagedGroups.map((g) => {
                      const key = g.serviceName;
                      const isOpen = expanded.has(key);
                      const { selected, total } = groupCounts(g);
                      const allSelected = selected === total && total > 0;

                      return (
                        <div key={key} className="border rounded mb-2">
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-decoration-none text-start fw-bold text-dark"
                              onClick={() => toggleExpanded(key)}
                            >
                              <i
                                className={`bi ${
                                  isOpen ? 'bi-chevron-down' : 'bi-chevron-right'
                                } me-2`}
                              />
                              {g.serviceName}{' '}
                              <span className="badge bg-secondary ms-1">
                                {selected}/{total}
                              </span>
                              {g.institutionName && (
                                <small className="text-muted ms-2 fw-normal">
                                  {g.institutionName}
                                </small>
                              )}
                            </button>
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-dark btn-sm"
                                onClick={() => setGroup(g, true)}
                                disabled={allSelected}
                              >
                                {t('SelectAll')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setGroup(g, false)}
                                disabled={selected === 0}
                              >
                                {t('ClearGroup')}
                              </button>
                            </div>
                          </div>
                          {isOpen && (
                            <div className="p-2">
                              {g.transactions.map((tx) => {
                                const txKey = tx.className.toUpperCase();
                                const checked = pending.has(txKey);
                                return (
                                  <div className="form-check ms-2" key={tx.className}>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`allowed-${tx.className}`}
                                      checked={checked}
                                      onChange={() => toggleClass(tx.className)}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`allowed-${tx.className}`}
                                    >
                                      <span className="fw-semibold">{tx.className}</span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}

                  {editGroups.length > pageSize && (
                    <div className="mt-3">
                      <Pagination
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        totalItems={editGroups.length}
                        pageSize={pageSize}
                        onPageSizeChange={(size) => {
                          setPageSize(size);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  )}

                  {orphanAllowed.length > 0 && (
                    <div className="alert alert-warning small mt-3 mb-0">
                      <div className="fw-bold mb-1">
                        {t('AllowedTransactionsUnknown')}
                      </div>
                      <ul className="mb-0 ps-3">
                        {orphanAllowed.map((c) => (
                          <li key={c}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="modal-footer d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    {t('AllowedTransactionsSelectedCount', { count: pending.size })}
                  </small>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                      disabled={saving}
                    >
                      {t('Cancel')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? t('Loading') : t('Save')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}
